import { useSupportModalStore } from '../stores/useSupportModalStore';
import type { ErrorMetadata } from '../stores/useSupportModalStore';
import { SUBCATEGORIES } from '../components/common/support/supportApi';
import { getPageLocation } from './getPageLocation';

const BUG_SUBCATEGORY_ID = SUBCATEGORIES[0].id;

export interface OpenSupportWithErrorOptions {
  /** Человекочитаемое место ошибки, например "Планирование продаж / Этап 1".
   *  Если не передано — определяется автоматически из URL. */
  location?: string;
  /** Название таблицы (добавляется к location через " — ") */
  tableTitle?: string;
  errorMessage: string;
  errorId?: string;
  /** HTTP статус (400, 500 и т.д.) */
  httpStatus?: number;
  /** Correlation ID из ответа бэкенда */
  correlationId?: string;
  /** Operation ID из ответа бэкенда */
  operationId?: string;
  /** Stack trace ошибки */
  stack?: string;
}

/** Собирает информацию о браузере */
function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  // Пробуем извлечь понятное название
  if (ua.includes('Edg/')) return `Edge ${ua.split('Edg/')[1]?.split(' ')[0] ?? ''}`;
  if (ua.includes('Chrome/')) return `Chrome ${ua.split('Chrome/')[1]?.split(' ')[0] ?? ''}`;
  if (ua.includes('Firefox/')) return `Firefox ${ua.split('Firefox/')[1]?.split(' ')[0] ?? ''}`;
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return `Safari`;
  return ua.slice(0, 80);
}

/**
 * Открывает SupportTicketModal с предзаполненным контекстом ошибки.
 * Может вызываться из любого места, включая не-React код.
 */
export function openSupportWithError(options: OpenSupportWithErrorOptions): void {
  const { errorMessage, errorId, tableTitle, httpStatus, correlationId, operationId, stack } = options;
  const location = options.location ?? getPageLocation(tableTitle);

  const title = `Ошибка: ${location}`.slice(0, 100);

  // Краткое описание для пользователя (он может дополнить)
  const description = errorMessage.slice(0, 300);

  // Структурированные метаданные — read-only, дописываются автоматически
  const metadata: ErrorMetadata = {
    errorId,
    timestamp: new Date().toLocaleString('ru-RU'),
    page: location,
    url: window.location.href,
    tableTitle,
    httpStatus,
    correlationId,
    operationId,
    errorMessage: errorMessage.slice(0, 200),
    stack: stack?.slice(0, 500),
    browser: getBrowserInfo(),
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
  };

  // Убираем undefined-поля
  for (const key of Object.keys(metadata) as (keyof ErrorMetadata)[]) {
    if (metadata[key] === undefined) delete metadata[key];
  }

  useSupportModalStore.getState().openWithError({
    subcategoryId: BUG_SUBCATEGORY_ID,
    title,
    description,
    metadata,
  });
}

/**
 * Форматирует метаданные ошибки в строку для отображения в UI (без лимита).
 */
export function formatErrorMetadata(metadata: ErrorMetadata): string {
  return buildMetadataLines(metadata).join('\n');
}

/**
 * Форматирует метаданные для вставки в описание тикета с учётом лимита символов.
 * Поля добавляются по приоритету: сначала самые ценные для диагностики,
 * длинные поля (stack, URL) — в конце и обрезаются если не влезают.
 *
 * @param metadata — метаданные ошибки
 * @param budget  — максимальное кол-во символов (уже за вычетом описания пользователя)
 */
export function formatErrorMetadataCompact(metadata: ErrorMetadata, budget: number): string {
  if (budget <= 0) return '';

  const SEPARATOR = '\n---\n';
  let remaining = budget - SEPARATOR.length;
  if (remaining <= 0) return '';

  // Поля в порядке приоритета для диагностики
  const prioritized: [string, string | undefined][] = [
    ['Correlation ID', metadata.correlationId],
    ['Operation ID', metadata.operationId],
    ['Error ID', metadata.errorId],
    ['HTTP', metadata.httpStatus?.toString()],
    ['Ошибка', metadata.errorMessage],
    ['Время', metadata.timestamp],
    ['Страница', metadata.page],
    ['Браузер', metadata.browser],
    ['Разрешение', metadata.screenSize],
    ['Таблица', metadata.tableTitle],
    ['URL', metadata.url],
    ['Stack', metadata.stack],
  ];

  const lines: string[] = [];

  for (const [label, value] of prioritized) {
    if (!value) continue;
    const line = `${label}: ${value}`;
    // +1 за \n между строками
    const cost = line.length + (lines.length > 0 ? 1 : 0);
    if (cost > remaining) {
      // Пробуем обрезать значение, если влезает хотя бы 20 символов
      const labelCost = label.length + 2; // "Label: "
      const availableForValue = remaining - labelCost - (lines.length > 0 ? 1 : 0);
      if (availableForValue >= 20) {
        lines.push(`${label}: ${value.slice(0, availableForValue - 1)}…`);
        remaining = 0;
      }
      break;
    }
    lines.push(line);
    remaining -= cost;
  }

  if (lines.length === 0) return '';
  return SEPARATOR + lines.join('\n');
}

/** Собирает все строки метаданных (для UI-отображения без лимита) */
function buildMetadataLines(metadata: ErrorMetadata): string[] {
  const lines: string[] = ['---', 'Техническая информация:'];

  if (metadata.errorId) lines.push(`Error ID: ${metadata.errorId}`);
  if (metadata.timestamp) lines.push(`Время: ${metadata.timestamp}`);
  if (metadata.page) lines.push(`Страница: ${metadata.page}`);
  if (metadata.url) lines.push(`URL: ${metadata.url}`);
  if (metadata.tableTitle) lines.push(`Таблица: ${metadata.tableTitle}`);
  if (metadata.httpStatus) lines.push(`HTTP статус: ${metadata.httpStatus}`);
  if (metadata.correlationId) lines.push(`Correlation ID: ${metadata.correlationId}`);
  if (metadata.operationId) lines.push(`Operation ID: ${metadata.operationId}`);
  if (metadata.errorMessage) lines.push(`Ошибка: ${metadata.errorMessage}`);
  if (metadata.browser) lines.push(`Браузер: ${metadata.browser}`);
  if (metadata.screenSize) lines.push(`Разрешение: ${metadata.screenSize}`);
  if (metadata.stack) lines.push(`\nStack:\n${metadata.stack}`);

  return lines;
}
